import DOMPurify from 'dompurify';

/**
 * SNAP-2297 / SNAP-2304 / SNAP-2305 — sanitize HTML before it is rendered via v-html
 * (article comments, AI Writer preview). Any stored HTML must go through this before
 * being interpolated raw into the DOM.
 *
 * `ADD_ATTR: ['target']` is required: DOMPurify strips `target` by default, and Tiptap's
 * Link extension sets `target="_blank"` for external links, so a bare `DOMPurify.sanitize()`
 * would silently break "open in new tab" on every comment.
 *
 * Do not reorder or re-quote attributes on the sanitizer output for mention spans — the
 * backend's ArticleMentionsService parses `data-type="mention" ... data-id="..."` with an
 * attribute-order-sensitive regex. DOMPurify does not reorder attributes, but keep this in
 * mind if this helper is ever changed to post-process its output.
 *
 * ## Why there is a style allowlist here too
 *
 * The backend sanitizer (`helpers/sanitizeCommentHtml.ts`) enforces `COMMENT_STYLES`, but it
 * only runs on WRITE — every comment stored before it shipped is still served raw. DOMPurify
 * permits the `style` attribute by default, so without this filter a legacy row could still
 * paint a full-page overlay over the app:
 *
 *   <p style="position:fixed;top:0;left:0;width:100vw;height:100vh;z-index:9999;
 *             background:#fff">Session expired — sign in</p>
 *
 * That is a credible credential-phishing surface, and on the read path this helper is the
 * only thing between the stored row and the reader. The list below MIRRORS the backend's
 * `COMMENT_STYLES` — keep the two in sync; a property allowed there but not here is silently
 * stripped from legitimate content, and one allowed here but not there is a hole.
 */

// Colours: hex, rgb()/rgba() or a named colour. Never url() or expression().
const COLOR = /^(#[0-9a-fA-F]{3,8}|rgba?\(\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*\d{1,3}\s*(,\s*[\d.]+\s*)?\)|[a-zA-Z]+)$/;
const LENGTH = /^\d+(\.\d+)?(px|em|rem|pt|%)$/;

// Inline styles Tiptap legitimately produces: TextAlign, Indent, Color, Highlight,
// FontFamily, FontSize. Mirrors COMMENT_STYLES in the backend's sanitizeCommentHtml.ts.
const ALLOWED_STYLES: Record<string, RegExp> = {
  'text-align': /^(left|right|center|justify)$/,
  'margin-left': LENGTH,
  color: COLOR,
  'background-color': COLOR,
  'font-family': /^[\w\s'",-]+$/,
  'font-size': LENGTH,
};

/**
 * Rebuild the `style` attribute from only the declarations on the allowlist.
 *
 * The raw attribute string is parsed rather than the CSSOM (`el.style`) on purpose: the CSSOM
 * expands shorthands, so `background: #fff` would surface as `background-color` and sneak past
 * a list that deliberately allows only the longhand.
 */
const filterInlineStyles = (node: Element): void => {
  if (typeof node.hasAttribute !== 'function' || !node.hasAttribute('style')) {
    return;
  }

  const kept = (node.getAttribute('style') || '')
    .split(';')
    .map((declaration) => {
      const separator = declaration.indexOf(':');
      if (separator === -1) {
        return null;
      }
      const property = declaration.slice(0, separator).trim().toLowerCase();
      const value = declaration.slice(separator + 1).trim();
      const pattern = ALLOWED_STYLES[property];

      return pattern && pattern.test(value) ? `${property}: ${value}` : null;
    })
    .filter((declaration): declaration is string => declaration !== null);

  if (kept.length) {
    node.setAttribute('style', kept.join('; '));
  } else {
    node.removeAttribute('style');
  }
};

const styleHook = (node: Node): void => filterInlineStyles(node as Element);

export const sanitizeHtml = (html: string): string => {
  if (!html) {
    return '';
  }

  // Registered per call rather than once at module load: DOMPurify's hook registry is a global
  // singleton, so a module-level addHook would silently apply this policy to every other
  // DOMPurify caller in the app — but only if this module happened to be imported first. That
  // makes behaviour depend on bundler load order. sanitize() is synchronous, so add/remove
  // around the call is deterministic and scoped.
  DOMPurify.addHook('afterSanitizeAttributes', styleHook);
  try {
    return DOMPurify.sanitize(html, { ADD_ATTR: ['target'] });
  } finally {
    DOMPurify.removeHook('afterSanitizeAttributes');
  }
};
